import React, { useState } from 'react';

import { RotateCcw, X } from 'lucide-react';

import { fetchAccountSearch } from '../api';
import { COHORT_ATTRIBUTE_OPTIONS } from '../constants';
import { ActiveAccount, CohortAttribute, CohortMode, SelectedAccount } from '../types';

import ActiveAccountsChecklist from './ActiveAccountsChecklist';

type ActiveAccountsState = {
  data: ActiveAccount[] | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  refetch: () => unknown;
};

type CohortAccountSelectorProps = {
  mode: CohortMode | '';
  onModeChange: (mode: CohortMode) => void;
  excludedAccountIds: string[];
  onExcludedAccountIdsChange: (ids: string[]) => void;
  attribute: CohortAttribute;
  onAttributeChange: (attribute: CohortAttribute) => void;
  selectedAccounts: SelectedAccount[];
  onSelectedAccountsChange: (accounts: SelectedAccount[]) => void;
  activeAccounts: ActiveAccountsState;
  disabled?: boolean;
  modeLabel?: string;
  modePlaceholder?: string;
};

const CohortAccountSelector: React.FC<CohortAccountSelectorProps> = ({
  mode,
  onModeChange,
  excludedAccountIds,
  onExcludedAccountIdsChange,
  attribute,
  onAttributeChange,
  selectedAccounts,
  onSelectedAccountsChange,
  activeAccounts,
  disabled = false,
  modeLabel = 'Cambiar modo',
  modePlaceholder = 'Selecciona una cohorte…',
}) => {
  const [accountQuery, setAccountQuery] = useState('');
  const [accountResults, setAccountResults] = useState<SelectedAccount[]>([]);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);

  const searchAccounts = async (q: string): Promise<void> => {
    if (q.trim().length < 2) {
      setAccountResults([]);
      return;
    }
    setIsSearchingAccounts(true);
    try {
      const res = await fetchAccountSearch(q);
      setAccountResults(res.data.results.map((a) => ({ id: a.id, name: a.name })));
    } catch {
      setAccountResults([]);
    } finally {
      setIsSearchingAccounts(false);
    }
  };

  const addAccountToManual = (account: SelectedAccount): void => {
    if (!selectedAccounts.find((a) => a.id === account.id)) {
      onSelectedAccountsChange([...selectedAccounts, account]);
    }
    setAccountQuery('');
    setAccountResults([]);
  };

  return (
    <>
      <label className="block">
        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          {modeLabel}
        </span>
        <select
          className="input mt-0.5 w-full text-sm"
          value={mode}
          onChange={(e) => {
            setAccountResults([]);
            setAccountQuery('');
            onModeChange(e.target.value as CohortMode);
          }}
          disabled={disabled}
        >
          <option value="" disabled>
            {modePlaceholder}
          </option>
          <option value="all">Todas las cuentas activas</option>
          <option value="automatic">Por atributo (automático)</option>
          <option value="manual">Manual (cuentas específicas)</option>
        </select>
      </label>

      {mode === 'all' && activeAccounts.isLoading && (
        <p className="text-xs text-gray-400">Cargando cuentas activas…</p>
      )}
      {mode === 'all' && activeAccounts.isError && (
        <div className="bg-red-50 flex items-center justify-between gap-2 p-2 text-xs text-red-600 rounded">
          <span>No se pudieron cargar las cuentas activas.</span>
          <button
            type="button"
            className="hover:text-red-700 inline-flex items-center gap-1 font-semibold"
            onClick={() => activeAccounts.refetch()}
          >
            <RotateCcw size={12} />
            Reintentar
          </button>
        </div>
      )}
      {mode === 'all' && activeAccounts.isSuccess && (
        <ActiveAccountsChecklist
          accounts={activeAccounts.data ?? []}
          excludedIds={excludedAccountIds}
          onChange={onExcludedAccountIdsChange}
          disabled={disabled}
        />
      )}

      {mode === 'automatic' && (
        <label className="block">
          <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
            Atributo
          </span>
          <select
            className="input mt-0.5 w-full text-sm"
            value={attribute}
            onChange={(e) => onAttributeChange(e.target.value as CohortAttribute)}
            disabled={disabled}
          >
            {COHORT_ATTRIBUTE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {mode === 'manual' && (
        <div className="space-y-2">
          <div className="relative">
            <input
              className="input w-full pr-8 text-sm"
              placeholder="Buscar cuenta por nombre…"
              value={accountQuery}
              onChange={(e) => {
                setAccountQuery(e.target.value);
                void searchAccounts(e.target.value);
              }}
            />
            {isSearchingAccounts && (
              <span className="right-2 top-1/2 absolute text-xs text-gray-400 -translate-y-1/2">
                …
              </span>
            )}
          </div>
          {accountResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto bg-white border border-gray-200 rounded shadow-sm">
              {accountResults.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-0"
                  onClick={() => addAccountToManual(a)}
                >
                  {a.name}
                  <span className="ml-1 text-xs text-gray-400">#{a.id}</span>
                </button>
              ))}
            </div>
          )}
          {selectedAccounts.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedAccounts.map((a) => (
                <span
                  key={a.id}
                  className="flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs text-blue-700"
                >
                  {a.name}
                  <button
                    type="button"
                    onClick={() =>
                      onSelectedAccountsChange(selectedAccounts.filter((x) => x.id !== a.id))
                    }
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CohortAccountSelector;
